//*Libraries imports
import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient();

/**
 * Invalidates multiple React Query cache entries based on the provided query keys.
 * This is useful when you need to refetch data after mutations or when cache needs to be cleared.
 *
 * @param queryKeys - An array of query key arrays. Each inner array represents a query key path.
 *
 * @example
 * ```ts
 * await queryInvalidator([
 * ["users"],
 * ["user", userId],
 * [QUERY_KEYS.USERS.SINGLE],
 * [QUERY_KEYS.USERS.ALL],
 * ]);
 * ```
 */
export async function queryInvalidator(queryKeys: (string | number)[][]) {
  const promises: Promise<void>[] = []; // array to store promises

  // loop through each query key array and invalidate the cache
  for (const queryKey of queryKeys) {
    promises.push(queryClient.invalidateQueries({ queryKey }));
  }

  // run all promises in parallel
  await Promise.all(promises);
}

export const QUERY_KEYS = {
  PRODUCTS: {
    ALL: "products",
    SINGLE: "product",
  },
  PHASE_FORMS: {
    ALL: "phase-forms",
    BY_PRODUCT: "phase-forms-by-product",
  },
  SALES: {
    ALL: "sales",
    SINGLE: "sale",
  },
} as const;

type ExtractValues<T> = T extends string
  ? T
  : T extends Record<string | number | symbol, unknown>
    ? ExtractValues<T[keyof T]>
    : never;

export type QueryKeyValues = ExtractValues<typeof QUERY_KEYS>;
